import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X, Search, Plus, Check } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { ClinicalGroup, ClinicalOption } from '@/lib/clinical-dictionaries';
import { matchesQuery } from '@/lib/clinical-dictionaries';

/**
 * SearchableSelect
 *
 * Industrial-efficient replacement for the big button-grid pickers in
 * clinical fields. Flow is vertical and dense: one compact row that
 * expands into a searchable dropdown. Keyboard-first (arrow keys, enter,
 * escape). Works for both single-value and multi-value fields.
 *
 * Design rules baked in:
 *   - Single compact row when collapsed (no button sea)
 *   - Type to filter; first match highlights automatically
 *   - Arrow keys navigate, Enter selects, Escape closes
 *   - Grouped options stay grouped in the dropdown (with collapsible headers)
 *   - Any on-screen entry is addable; typed-but-unmatched text offers
 *     "Add "<query>"" so providers never feel blocked by the list
 *   - Selected chips stack BELOW the input (vertical flow)
 *   - Multi-select: removable chips; single-select: clear button
 */

interface BaseProps {
  label?: string;
  placeholder?: string;
  groups: ClinicalGroup[];
  /** Allow the user to add off-list free-text entries. Default true. */
  allowFreeText?: boolean;
  /** Optional hint shown below the input. */
  hint?: string;
  disabled?: boolean;
  className?: string;
  /** DOM id for the input (accessibility). */
  id?: string;
}

interface SingleProps extends BaseProps {
  mode: 'single';
  value: string | null;
  onChange: (value: string | null) => void;
}

interface MultiProps extends BaseProps {
  mode: 'multi';
  value: string[];
  onChange: (value: string[]) => void;
  /** Show count of selected items in header. Default true for multi. */
  showCount?: boolean;
  /** Hide the chip stack below the input. Use when the parent
   *  renders each selection as its own editable row (meds, allergies)
   *  so the chip strip isn't a second, redundant view of the same data. */
  hideSelectedChips?: boolean;
}

type Props = SingleProps | MultiProps;

export function SearchableSelect(props: Props) {
  const { label, placeholder, groups, hint, disabled, className, id } = props;
  const allowFreeText = props.allowFreeText !== false;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Build filtered, flat list preserving group headers for rendering.
  const filtered = useMemo(() => {
    if (!query.trim()) return groups;
    return groups
      .map((g) => ({
        category: g.category,
        options: g.options.filter((o) => matchesQuery(o, query)),
      }))
      .filter((g) => g.options.length > 0);
  }, [groups, query]);

  // Flatten for keyboard navigation (cursor indexes into this).
  const flat: ClinicalOption[] = useMemo(
    () => filtered.flatMap((g) => g.options),
    [filtered],
  );

  // Reset cursor when query changes
  useEffect(() => { setCursor(0); }, [query]);

  // Keep selected cursor in view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-cursor="${cursor}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  function commit(value: string) {
    if (props.mode === 'single') {
      props.onChange(value);
      setOpen(false);
      setQuery('');
    } else {
      if (!props.value.includes(value)) props.onChange([...props.value, value]);
      setQuery('');
      // keep open for rapid multi-add
      inputRef.current?.focus();
    }
  }

  function remove(value: string) {
    if (props.mode === 'single') props.onChange(null);
    else props.onChange(props.value.filter((v) => v !== value));
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setCursor((c) => Math.min(c + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flat[cursor]) commit(flat[cursor].label);
      else if (allowFreeText && query.trim()) commit(query.trim());
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    } else if (e.key === 'Backspace' && !query && props.mode === 'multi' && props.value.length > 0) {
      // Quick-remove last chip with backspace on empty input
      props.onChange(props.value.slice(0, -1));
    }
  }

  const selectedList = props.mode === 'single'
    ? (props.value ? [props.value] : [])
    : props.value;

  const showFreeTextRow = allowFreeText
    && query.trim().length > 0
    && !flat.some((o) => o.label.toLowerCase() === query.trim().toLowerCase());

  return (
    <div className={cn('w-full', className)} ref={wrapRef}>
      {label && (
        <label htmlFor={id} className="data-label mb-1 block">
          {label}
          {props.mode === 'multi' && (props.showCount ?? true) && selectedList.length > 0 && (
            <span className="ml-2 font-mono text-slate-400">· {selectedList.length}</span>
          )}
        </label>
      )}

      {/* Compact input row */}
      <div
        className={cn(
          'input-field flex items-center gap-2 px-2 py-1.5 cursor-text',
          open && 'ring-1 ring-blue-400 border-blue-500',
          disabled && 'opacity-60 pointer-events-none',
        )}
        onClick={() => { inputRef.current?.focus(); setOpen(true); }}
      >
        <Search size={14} className="text-slate-500 shrink-0" />
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={
            props.mode === 'single' && props.value
              ? props.value
              : (placeholder ?? 'Search or type…')
          }
          className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm text-slate-100 placeholder:text-slate-500"
          autoComplete="off"
          disabled={disabled}
        />
        {props.mode === 'single' && props.value && !query && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); props.onChange(null); }}
            className="text-slate-500 hover:text-slate-200"
            aria-label="Clear"
          >
            <X size={14} />
          </button>
        )}
        <ChevronDown
          size={14}
          className={cn('text-slate-500 transition-transform', open && 'rotate-180')}
        />
      </div>

      {/* Selected chips — stacked BELOW the input (vertical flow).
          Suppressed when hideSelectedChips is set (meds, allergies —
          where each selection gets its own editable row elsewhere). */}
      {props.mode === 'multi' && !props.hideSelectedChips && selectedList.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {selectedList.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-sm border border-blue-700 bg-blue-950/40 px-2 py-0.5 text-xs text-blue-200"
            >
              {v}
              <button
                type="button"
                onClick={() => remove(v)}
                className="text-blue-400 hover:text-blue-100"
                aria-label={`Remove ${v}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown */}
      {open && (
        <div
          ref={listRef}
          className="relative z-30 mt-1 max-h-72 overflow-y-auto rounded-sm border border-slate-700 bg-slate-900 shadow-lg"
        >
          {filtered.length === 0 && !showFreeTextRow && (
            <div className="px-3 py-4 text-center text-xs text-slate-500">
              No matches
            </div>
          )}

          {filtered.map((group) => (
            <div key={group.category}>
              <div className="sticky top-0 bg-slate-950 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-800">
                {group.category}
              </div>
              {group.options.map((opt) => {
                const idx = flat.indexOf(opt);
                const isCursor = idx === cursor;
                const isSelected = selectedList.includes(opt.label);
                return (
                  <button
                    key={opt.label}
                    type="button"
                    data-cursor={idx}
                    onMouseEnter={() => setCursor(idx)}
                    onClick={(e) => { e.stopPropagation(); commit(opt.label); }}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm',
                      isCursor ? 'bg-blue-950/50 text-blue-100' : 'text-slate-200 hover:bg-slate-800',
                      isSelected && 'text-blue-300',
                    )}
                  >
                    <span className="truncate">{opt.label}</span>
                    {isSelected && <Check size={14} className="text-blue-400 shrink-0" />}
                  </button>
                );
              })}
            </div>
          ))}

          {showFreeTextRow && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); commit(query.trim()); }}
              className="flex w-full items-center gap-2 border-t border-slate-800 bg-slate-950 px-3 py-2 text-left text-sm text-amber-300 hover:bg-slate-800"
            >
              <Plus size={14} />
              Add &ldquo;{query.trim()}&rdquo;
              <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-slate-500">
                Enter
              </span>
            </button>
          )}
        </div>
      )}

      {hint && <p className="mt-1 text-[10px] text-slate-500">{hint}</p>}
    </div>
  );
}
